import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TopNavBar from '../components/common/TopNavBar';
import '../styles/Login.css';

const API = process.env.REACT_APP_API_URL;
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  accept: 'application/json',
});

const getTagLabel = (name) => {
  if (!name) return '';
  if (typeof name === 'object') return name.vi || name.en || Object.values(name)[0] || '';
  return name;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const userName = localStorage.getItem('userFullName') || 'Bạn';
  const userAvatar = localStorage.getItem('userAvatar') || '🦊';

  const [streakInfo, setStreakInfo] = useState(null);
  const [starsInfo, setStarsInfo] = useState(null);
  const [questsData, setQuestsData] = useState(null);
  const [performanceMap, setPerformanceMap] = useState(null);
  const [weakTags, setWeakTags] = useState(null); // null = loading, [] = no data
  const [loadingPerf, setLoadingPerf] = useState(true);

  // TODO: Replace with real API
  const recentActivity = [
    { type: 'quiz', name: 'Mini Quiz: Animals', score: '10/10', label: 'EXCELLENT', time: '2 giờ trước', color: 'text-hupath-scout' },
    { type: 'test', name: 'Full Test: Movers 03', score: '8.5', label: 'GOOD JOB', time: 'Hôm qua, 15:30', color: 'text-hupath-gold' },
    { type: 'quiz', name: 'Mini Quiz: Food', score: '9/10', label: 'GREAT', time: '03/04/2026', color: 'text-hupath-scout' },
    { type: 'quiz', name: 'Mini Quiz: Clothing', score: '4/10', label: 'KEEP TRYING', time: '02/04/2026', color: 'text-red-500' },
  ];

  const today = new Date().toLocaleDateString('vi-VN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // Gamification
    Promise.all([
      fetch(`${API}/client/streak/`, { headers: authHeader() }),
      fetch(`${API}/client/stars/`, { headers: authHeader() }),
      fetch(`${API}/client/daily-quests/`, { headers: authHeader() }),
    ]).then(async ([sr, st, qr]) => {
      if (sr.ok) setStreakInfo(await sr.json());
      if (st.ok) setStarsInfo(await st.json());
      if (qr.ok) setQuestsData(await qr.json());
    }).catch(() => {});

    // Performance
    Promise.all([
      fetch(`${API}/users/me/performance-map`, { headers: authHeader() }),
      fetch(`${API}/users/me/performance-tags?tag_type=grammar`, { headers: authHeader() }),
      fetch(`${API}/users/me/performance-tags?tag_type=vocabulary_topic`, { headers: authHeader() }),
    ]).then(async ([mapRes, gramRes, vocaRes]) => {
      if (mapRes.ok) setPerformanceMap(await mapRes.json());
      const gram = gramRes.ok ? (await gramRes.json()).data || [] : [];
      const voca = vocaRes.ok ? (await vocaRes.json()).data || [] : [];
      const allTags = [...gram, ...voca].filter(t => t.total_attempts > 0);
      setWeakTags(allTags.sort((a, b) => a.mastery_score - b.mastery_score).slice(0, 3));
    }).catch(() => setWeakTags([])).finally(() => setLoadingPerf(false));
  }, []);

  // Derived values from performance map
  const getSkill = (code) => performanceMap?.skills?.find(s => s.code === code);
  const listeningScore = Math.round((getSkill('listening')?.average_score || 0) * 100);
  const rwScore = Math.round((getSkill('reading_writing')?.average_score || 0) * 100);
  const hasPerformanceData =
    (getSkill('listening')?.total_attempts || 0) > 0 ||
    (getSkill('reading_writing')?.total_attempts || 0) > 0;
  const overallScore = hasPerformanceData ? Math.round((listeningScore + rwScore) / 2) : 0;

  // Weak parts (exercise types) from performance map for suggestions
  const weakParts = performanceMap
    ? performanceMap.skills
        .flatMap(s => s.parts.map(p => ({ ...p, skillName: s.name })))
        .filter(p => p.total_attempts > 0)
        .sort((a, b) => a.score - b.score)
        .slice(0, 3)
    : [];

  const donutCircumference = 364;
  const donutOffset = donutCircumference - (overallScore / 100) * donutCircumference;

  const scoreColor = (s) => s >= 70 ? 'text-hupath-scout' : s >= 50 ? 'text-hupath-gold' : 'text-red-500';
  const barColor = (s) => s >= 70 ? 'bg-hupath-scout' : s >= 50 ? 'bg-hupath-gold' : 'bg-red-400';

  const suggestionStyles = [
    { border: 'border-red-500', bg: 'bg-red-50/40 hover:bg-red-50', btn: 'text-red-500 hover:bg-red-500' },
    { border: 'border-hupath-gold', bg: 'bg-amber-50/40 hover:bg-amber-50', btn: 'text-hupath-gold hover:bg-hupath-gold' },
    { border: 'border-blue-400', bg: 'bg-blue-50/40 hover:bg-blue-50', btn: 'text-blue-500 hover:bg-blue-500' },
  ];

  return (
    <div className="min-h-screen ob-sky-voyager font-body text-hp-on-surface relative overflow-x-hidden hp-trail-pattern">
      <TopNavBar />

      <main className="max-w-6xl mx-auto px-6 py-8 relative z-10">

        {/* ── Block 1: Welcome + Gamification Stats ── */}
        <section className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6 mb-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center text-4xl shadow-inner">
              {userAvatar}
            </div>
            <div>
              <h1 className="text-3xl font-black text-hp-on-surface font-headline mb-1">
                Xin chào, {userName}! 👋
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-hp-on-surface/60 font-medium">{today}</p>
                <span className="bg-hupath-scout/10 text-hupath-scout px-3 py-0.5 rounded-full text-sm font-bold font-headline">
                  Level: Flyers ✈️
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="bg-orange-50 px-4 py-3 rounded-xl flex flex-col items-center min-w-[90px]">
              <span className="text-2xl mb-1">🔥</span>
              <span className="text-xs font-bold text-orange-600 font-headline uppercase tracking-wider">Streak</span>
              <span className="text-xl font-black text-orange-700 font-headline">
                {streakInfo ? `${streakInfo.current} ngày` : '—'}
              </span>
            </div>
            <div className="bg-amber-50 px-4 py-3 rounded-xl flex flex-col items-center min-w-[90px]">
              <span className="text-2xl mb-1">⭐</span>
              <span className="text-xs font-bold text-hupath-gold font-headline uppercase tracking-wider">Stars</span>
              <span className="text-xl font-black text-hupath-gold font-headline">
                {starsInfo ? starsInfo.total_stars : '—'}
              </span>
            </div>
            <div className="bg-green-50 px-4 py-3 rounded-xl flex flex-col items-center min-w-[90px]">
              <span className="text-2xl mb-1">🛡️</span>
              <span className="text-xs font-bold text-hupath-scout font-headline uppercase tracking-wider">Shields</span>
              <span className="text-xl font-black text-hupath-scout font-headline">
                {streakInfo ? streakInfo.shields : '—'}
              </span>
            </div>
          </div>
        </section>

        {/* ── Block 2: Daily Quests (compact) ── */}
        <section className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center text-lg">🎯</div>
              <h2 className="font-headline font-bold text-hp-on-surface">Nhiệm vụ hôm nay</h2>
            </div>
            {questsData && (
              <span className="text-sm font-bold text-hupath-scout font-headline">
                {questsData.bonus.completed_count}/3 {questsData.bonus.all_completed ? '🎉' : ''}
              </span>
            )}
          </div>

          {questsData ? (
            <div className="flex flex-col sm:flex-row gap-2">
              {questsData.quests.map((q) => (
                <div
                  key={q.id}
                  className={`flex-1 flex items-center gap-3 p-3 rounded-xl transition-all ${q.is_completed ? 'bg-green-50' : 'bg-gray-50'}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    q.is_completed ? 'bg-hupath-leaf text-white' : 'bg-white border-2 border-gray-200 text-gray-500'
                  }`}>
                    {q.is_completed ? '✓' : `${q.current_value}/${q.target_value}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium leading-tight ${q.is_completed ? 'text-hupath-scout line-through' : 'text-hp-on-surface'}`}>
                      {q.description}
                    </p>
                    {!q.is_completed && q.current_value > 0 && (
                      <div className="w-full bg-gray-200 h-1 rounded-full overflow-hidden mt-1.5">
                        <div
                          className="bg-hupath-leaf h-full rounded-full transition-all"
                          style={{ width: `${Math.min((q.current_value / q.target_value) * 100, 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <span className={`text-xs font-bold flex-shrink-0 ${q.is_completed ? 'text-hupath-scout' : 'text-hupath-gold'}`}>
                    +{q.stars_reward}⭐
                  </span>
                </div>
              ))}
              {questsData.bonus.all_completed && (
                <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-green-50 rounded-xl border-2 border-green-200">
                  <span className="text-lg">🎉</span>
                  <div>
                    <p className="text-xs font-bold text-hupath-scout">Bonus!</p>
                    <p className="text-xs text-hupath-scout">+{questsData.bonus.bonus_stars}⭐</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex-1 h-14 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          )}
        </section>

        {/* ── Block 3: Quick Actions (2 cards) ── */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div
            className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6 border-t-4 border-hupath-scout hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            onClick={() => navigate('/skill-practice')}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="text-4xl">📝</div>
              <div>
                <h3 className="text-xl font-black text-hp-on-surface font-headline mb-1">Luyện tập nhanh</h3>
                <p className="text-hp-on-surface/60 text-sm font-medium">AI chọn bài phù hợp • 5-10 phút</p>
              </div>
            </div>
            <p className="text-hupath-scout font-bold text-sm mb-5">1 click → vào quiz ngay! ⚡</p>
            <button className="w-full hp-button-gradient text-white py-3 rounded-xl font-headline font-bold text-lg hover:shadow-lg transition-all active:scale-95">
              Bắt đầu →
            </button>
          </div>

          <div
            className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6 border-t-4 border-hupath-gold hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            onClick={() => navigate('/full-mock-test')}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="text-4xl">📄</div>
              <div>
                <h3 className="text-xl font-black text-hp-on-surface font-headline mb-1">Thi thử Full đề</h3>
                <p className="text-hp-on-surface/60 text-sm font-medium">Mô phỏng đề thi thật • 45 phút</p>
              </div>
            </div>
            <p className="text-hupath-gold font-bold text-sm mb-5">Trải nghiệm phòng thi thật 🏆</p>
            <button className="w-full border-2 border-hupath-gold text-hupath-gold py-3 rounded-xl font-headline font-bold text-lg hover:bg-hupath-gold/5 transition-all active:scale-95">
              Chọn đề thi →
            </button>
          </div>
        </section>

        {/* ── Block 4: Insights ── */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">

          {/* Left col: Tiến độ + Điểm yếu */}
          <div className="lg:col-span-7 flex flex-col gap-6">

            {/* Tiến độ tổng quan */}
            <div className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-black text-hp-on-surface font-headline flex items-center gap-2">
                  <span className="material-symbols-outlined text-hupath-scout">analytics</span>
                  Tiến độ tổng quan
                </h3>
                <Link to="/library" className="text-hupath-scout font-bold text-sm hover:underline font-headline">
                  Xem Thư Viện →
                </Link>
              </div>

              {loadingPerf ? (
                <div className="flex items-center gap-8">
                  <div className="w-24 h-24 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-3">
                    <div className="h-4 bg-gray-100 rounded-full animate-pulse" />
                    <div className="h-2 bg-gray-100 rounded-full animate-pulse" />
                    <div className="h-4 bg-gray-100 rounded-full animate-pulse w-3/4" />
                    <div className="h-2 bg-gray-100 rounded-full animate-pulse" />
                  </div>
                </div>
              ) : !hasPerformanceData ? (
                <div className="text-center py-8">
                  <p className="text-4xl mb-3">🌱</p>
                  <p className="font-bold text-hp-on-surface/60">Chưa có dữ liệu học tập</p>
                  <p className="text-sm text-hp-on-surface/40 mt-1">Hãy làm bài để theo dõi tiến độ!</p>
                </div>
              ) : (
                <div className="flex items-center gap-8">
                  {/* Donut chart */}
                  <div className="relative w-28 h-28 flex-shrink-0 flex items-center justify-center">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 128 128">
                      <circle className="text-gray-100" cx="64" cy="64" fill="transparent" r="58"
                        stroke="currentColor" strokeWidth="12" />
                      <circle className="text-hupath-scout" cx="64" cy="64" fill="transparent" r="58"
                        stroke="currentColor" strokeDasharray={donutCircumference} strokeDashoffset={donutOffset}
                        strokeLinecap="round" strokeWidth="12"
                        style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
                    </svg>
                    <div className="absolute flex flex-col items-center">
                      <span className="text-2xl font-black text-hp-on-surface font-headline">{overallScore}%</span>
                      <span className="text-[9px] font-bold text-hp-on-surface/40 uppercase">Tổng</span>
                    </div>
                  </div>

                  {/* Skill bars */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <div className="flex justify-between mb-1.5">
                        <span className="text-sm font-bold text-hp-on-surface font-headline">🎧 Listening</span>
                        <span className={`text-sm font-black ${scoreColor(listeningScore)}`}>{listeningScore}%</span>
                      </div>
                      <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden">
                        <div
                          className={`${barColor(listeningScore)} h-full rounded-full transition-all duration-700`}
                          style={{ width: `${listeningScore}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1.5">
                        <span className="text-sm font-bold text-hp-on-surface font-headline">📖 Reading & Writing</span>
                        <span className={`text-sm font-black ${scoreColor(rwScore)}`}>{rwScore}%</span>
                      </div>
                      <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden">
                        <div
                          className={`${barColor(rwScore)} h-full rounded-full transition-all duration-700`}
                          style={{ width: `${rwScore}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Điểm yếu */}
            <div className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6">
              <h3 className="text-xl font-black text-hp-on-surface font-headline flex items-center gap-2 mb-5">
                <span className="text-xl">⚠️</span>
                Điểm yếu cần cải thiện
              </h3>

              {weakTags === null ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : weakTags.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-4xl mb-3">🎉</p>
                  <p className="font-bold text-hupath-scout">Tốt lắm! Chưa phát hiện điểm yếu</p>
                  <p className="text-sm text-hp-on-surface/40 mt-1">Tiếp tục làm bài để nhận phân tích chi tiết hơn</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {weakTags.map((tag) => {
                    const score = Math.round(tag.mastery_score * 100);
                    const label = getTagLabel(tag.tag_name);
                    return (
                      <div key={tag.tag_id} className="flex items-center gap-4 p-3 bg-red-50/50 rounded-xl border border-red-100">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-bold text-sm text-hp-on-surface truncate">{label}</span>
                            <span className="text-red-500 font-black text-sm ml-2 flex-shrink-0">{score}%</span>
                          </div>
                          <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                            <div className="bg-red-400 h-full rounded-full" style={{ width: `${score}%` }} />
                          </div>
                          <p className="text-xs text-hp-on-surface/40 mt-1">Đã luyện {tag.total_attempts} lần</p>
                        </div>
                        <button
                          onClick={() => navigate('/skill-practice')}
                          className="flex-shrink-0 text-xs font-bold text-red-500 bg-white border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-500 hover:text-white transition-all"
                        >
                          Luyện →
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right col: Gợi ý hôm nay */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6 h-full flex flex-col">
              <h3 className="text-xl font-black text-hp-on-surface font-headline mb-5 flex items-center gap-2">
                <span className="material-symbols-outlined text-hupath-gold" style={{ fontVariationSettings: "'FILL' 1" }}>
                  lightbulb
                </span>
                Gợi ý cho con hôm nay
              </h3>

              {loadingPerf ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : weakParts.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
                  <p className="text-4xl mb-3">🌱</p>
                  <p className="font-bold text-hp-on-surface/60">Chưa có gợi ý cá nhân hoá</p>
                  <p className="text-sm text-hp-on-surface/40 mt-1">Hãy làm thêm bài để AI phân tích!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {weakParts.map((part, i) => {
                    const score = Math.round(part.score * 100);
                    const s = suggestionStyles[i % 3];
                    return (
                      <div
                        key={part.code}
                        className={`p-4 border-l-4 ${s.border} ${s.bg} rounded-r-xl flex items-center justify-between transition-colors`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <h4 className="font-bold text-hp-on-surface font-headline text-sm">{part.name}</h4>
                            <span className="text-[10px] font-bold text-hp-on-surface/40 bg-gray-100 px-1.5 py-0.5 rounded">
                              {part.skillName}
                            </span>
                          </div>
                          <p className="text-xs text-hp-on-surface/60">{score}% • {part.total_attempts} lần làm</p>
                        </div>
                        <button
                          onClick={() => navigate('/skill-practice')}
                          className={`flex-shrink-0 ml-3 bg-white px-3 py-1.5 rounded-lg text-xs font-bold ${s.btn} shadow-sm hover:text-white transition-all`}
                        >
                          Luyện
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Block 5: Recent Activity (full-width) ── */}
        <section className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6 mb-8">
          <div className="flex justify-between items-center mb-5">
            <h3 className="text-xl font-black text-hp-on-surface font-headline flex items-center gap-2">
              <span className="material-symbols-outlined text-hupath-scout">history</span>
              Hoạt động gần đây
            </h3>
            <Link to="/recent-activity" className="text-hupath-scout font-bold text-sm hover:underline font-headline">
              Xem tất cả →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {recentActivity.map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  item.type === 'quiz' ? 'bg-hupath-scout/10' : 'bg-hupath-gold/10'
                }`}>
                  {item.type === 'quiz' ? '📝' : '📄'}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-hp-on-surface font-headline truncate">{item.name}</h4>
                  <p className="text-[10px] text-hp-on-surface/40">{item.time}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`${item.color} font-black text-base font-headline`}>{item.score}</span>
                  <p className={`text-[10px] ${item.color}/60 font-bold`}>{item.label}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

      </main>

      {/* Background decorations */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[150px] bg-white/60 blur-[60px] rounded-[100%] pointer-events-none -z-10" />
      <div className="fixed top-20 right-[-100px] w-64 h-64 bg-amber-200/20 blur-[80px] rounded-full pointer-events-none -z-10" />
      <div className="fixed bottom-20 left-[-100px] w-80 h-80 bg-hupath-scout/10 blur-[100px] rounded-full pointer-events-none -z-10" />
    </div>
  );
};

export default Dashboard;
